/**
 * The send's landing, shared by the admin chat screen and the client's Secure room (chat-relay-reply
 * D5, D10): the bubble appears at once at the newest end, then, once it is laid out, the typed
 * words travel into it (sendLanding). The screen queues the landing with the bubble's key right
 * after the send; the landing reports when it settles, so a failure line waits for it
 * (lib/chat/landingHold).
 */
import { useCallback, useLayoutEffect, useRef, type RefObject } from 'react';
import { landingSettled } from '@/lib/chat/landingHold';
import { playLanding, type SendOrigin } from './sendLanding';

interface SendLandingOptions {
  /** Positioned ancestor the travelling words are drawn in. */
  stageRef: RefObject<HTMLElement>;
  /** The thread's content box, which holds the bubbles and slides up as one arrives. */
  tailRef: RefObject<HTMLElement>;
  /** The rendered entries: the landing starts on the render that lays its bubble out. */
  entries: readonly unknown[];
  reduced: boolean;
  /** The travelling words' color: the skin's primary text (admin by default). */
  ghostColor?: string;
}

/** Returns `queue(key, text, origin)`: call it with the key the send returned. */
export function useSendLanding({ stageRef, tailRef, entries, reduced, ghostColor }: SendLandingOptions) {
  const queuedRef = useRef<{ key: string; text: string; origin: SendOrigin | null } | null>(null);

  const queue = useCallback((key: string, text: string, origin: SendOrigin | null) => {
    queuedRef.current = { key, text, origin };
  }, []);

  useLayoutEffect(() => {
    const queued = queuedRef.current;
    const stage = stageRef.current;
    const tail = tailRef.current;
    if (!queued || !stage || !tail) return;
    const bubble = Array.from(tail.querySelectorAll<HTMLElement>('[data-entry-id]')).find((node) => node.dataset.entryId === queued.key);
    if (!bubble) return;
    queuedRef.current = null;
    landingSettled(queued.key, playLanding({ stage, tail, bubble, text: queued.text, origin: queued.origin, reduced, ghostColor }));
  }, [entries, ghostColor, reduced, stageRef, tailRef]);

  return queue;
}
